import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { IoSettingsOutline, IoChevronDown } from "react-icons/io5";
import { FaRegCircleUser, FaMoon, FaSun } from "react-icons/fa6";
import { MdOutlineDashboard } from "react-icons/md";
import Notification from "./Notification";
import NotificationBell from "./Notification/NotificationBell";
import Profile from "./Profile";
import NavDropdown from "./NavDropdown";
import { useTheme } from "../ThemeContext";
import { useSidebar } from "./SidebarContext";
import { getSnagStats, resolveActiveProjectId } from "../api";
import { initializeActiveProject } from "../utils/projectInitializer";
import { setSelectedProject } from "../store/userSlice";
import store from "../store/store";

const ORANGE = "#ffbe63";
const BG_OFFWHITE = "#fcfaf7";
const SIDEBAR_WIDTH = 240;
const DESKTOP_HEADER_HEIGHT = 96;
const MOBILE_HEADER_HEIGHT = 64;
const HEADER_TOP_ROW_HEIGHT = 52;
const HEADER_NAV_ROW_HEIGHT = 44;

function Header() {
  const [isNotification, setIsNotification] = useState(false);
  const [isProfile, setIsProfile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  const { theme, toggleTheme } = useTheme();
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.location.pathname.startsWith("/project-head/dashboard")) {
      return;
    }
    const pid = localStorage.getItem("ACTIVE_PROJECT_ID");
    if (!pid || pid === "null" || pid === "undefined") {
      initializeActiveProject();
    }
  }, []);

  // ---- roles
  const rolee = localStorage.getItem("ROLE");
  let userRoles = [];
  try {
    userRoles = JSON.parse(localStorage.getItem("ACCESSES") || "[]")
      .flatMap((acc) => (Array.isArray(acc.roles) ? acc.roles : []))
      .map((r) => (typeof r === "string" ? r : r?.role));
  } catch {}
  const allRoles = [
    ...(rolee ? [rolee] : []),
    ...(userRoles.length ? userRoles : []),
  ];
  const ALLOWED_ROLES = ["admin", "super admin", "manager"];
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
  const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");

  const hasRole = (name) => allRoles.some((r) => norm(r) === norm(name));

  const hasAnyOrgAdminRole = () => {
    const normRoles = allRoles.map(normalize);
    return ALLOWED_ROLES.some((allowed) =>
      normRoles.includes(normalize(allowed)),
    );
  };

  const showHamburger = hasAnyOrgAdminRole();
  const allowuser = showHamburger;
  const isSecurityGuard = hasRole("security guard");
  // const showDmsInHeader = !showHamburger && !isSecurityGuard;
  const isProjectManagerOrHead =
    hasRole("Project Manager") || hasRole("Project Head") || hasRole("Head");
  const isSuperAdmin = hasRole("super admin");
  const isManager = hasRole("manager");
  const isAdmin = hasRole("admin");
  const showSafetyForUserOnly = !isManager && !isAdmin && !isSuperAdmin;

  // ---- Project Head dashboard visibility
  // Visible for "project head" (or "head") role — including users who ALSO
  // hold "checker"/"maker" roles at the same time (a multi-role user like a
  // Project Head who is also a Checker should still see this icon).
  // A small named-user whitelist is also supported for people who should
  // always see the icon regardless of role (e.g. vivek.gupta).
  const isProjectHead = hasRole("project head") || hasRole("head");

  const DASHBOARD_ICON_WHITELISTED_USERS = ["vivek.gupta"];
  const isDashboardIconWhitelistedUser = (() => {
    try {
      const raw = localStorage.getItem("USER_DATA");
      if (!raw) return false;
      const data = JSON.parse(raw);
      const candidates = [data?.username, data?.user_name, data?.email, data?.name]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      return candidates.some((id) => DASHBOARD_ICON_WHITELISTED_USERS.includes(id));
    } catch {
      return false;
    }
  })();

  const showProjectHeadDashboard = isProjectHead || isDashboardIconWhitelistedUser;

  // ---- theme colors
  const cardColor = theme === "dark" ? "#23232c" : "#fff";
  const borderColor = ORANGE;
  const textColor = theme === "dark" ? "#fff" : "#222";
  const iconColor = ORANGE;
  const navLinkStyle = { color: textColor, textDecoration: "none" };

  // ---- Grouped nav items: Quality + Safety
  const qualityNavItems = [
    { to: "/mir/create", label: "MIR", title: "Material Inspection Request" },
    { to: "/mir/inbox", label: "MIR Inbox", title: "My MIR Inbox" },
    { to: "/NCR", label: "NCR", title: "NON-CONFORMANCE REPORT" },
    { to: "/qc/observations", label: "QC Observation", title: "QC Observation" },
    ...(!isSecurityGuard
      ? [
          {
            to: isSuperAdmin ? "/forms" : "/project-forms",
            label: "Forms",
            title: isSuperAdmin
              ? "Forms Engine (Templates & Packs)"
              : "Project Forms",
          },
        ]
      : []),
  ];

  const safetyNavItems = [
    { to: "/safety/inspection-checker", label: "Safety Inspection", title: "Safety Inspection" },
    { to: "/safety/permit", label: "Permit", title: "Permit To Work" },
    { to: "/safety/observations", label: "Safety Observations", title: "Safety Observations" },
  ];

  const handleSettingsClick = () => {
    if (rolee && rolee.toLowerCase() === "super admin") {
      navigate("/user-management-setup");
    } else if (
      (rolee && rolee.toLowerCase() === "manager") ||
      allRoles.some((r) => norm(r) === "manager")
    ) {
      navigate("/user");
    } else {
      navigate("/setup");
    }
  };

  const handleDashboardClick = () => {
    // 1. Explicitly clear all project-specific keys from localStorage and sessionStorage
    const projectKeys = [
      "ACTIVE_PROJECT_ID",
      "SELECTED_PROJECT_ID",
      "PROJECT_ID",
      "project_id",
      "active_project_id",
      "SELECTED_PROJECT",
      "selected_project",
    ];
    projectKeys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // 2. Reset Redux store selectedProject if available
    try {
      store.dispatch(setSelectedProject(null));
    } catch (_) {}

    // 3. Dispatch custom event for real-time notification to any mounted dashboard
    window.dispatchEvent(
      new CustomEvent("konstruct:reset-dashboard-all-projects", {
        detail: { timestamp: Date.now() },
      })
    );

    // 4. Navigate to Dashboard with explicit reset state
    navigate("/project-head/dashboard", {
      state: {
        resetToAll: true,
        resetTimestamp: Date.now(),
      },
    });
  };

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-[200] w-full"
        style={{
          background: cardColor,
          borderBottom: `2px solid ${borderColor}`,
          color: textColor,
          transition: "background 0.3s",
        }}
      >
        {/* Top Row: Left Controls, Center Logo, Right Icons */}
        <div
          className="flex items-center justify-between px-4"
          style={{
            height: isMobile ? MOBILE_HEADER_HEIGHT : HEADER_TOP_ROW_HEIGHT,
            minHeight: isMobile ? MOBILE_HEADER_HEIGHT : HEADER_TOP_ROW_HEIGHT,
          }}
        >
          {/* Left: Hamburger + Home */}
          <div
            className="flex items-center gap-4 flex-1 justify-start"
            style={{
              minWidth: 0,
              marginLeft: sidebarOpen && showHamburger && !isMobile ? SIDEBAR_WIDTH : 0,
              transition: "margin-left 0.35s cubic-bezier(.6,-0.17,.22,1.08)",
            }}
          >
            {showHamburger && (
              <button
                onClick={() => setSidebarOpen((open) => !open)}
                className="mr-1 rounded-lg shadow-lg flex items-center justify-center"
                style={{
                  background: "#fff",
                  border: `2px solid ${borderColor}`,
                  width: 40,
                  height: 40,
                  color: iconColor,
                  transition: "background 0.2s",
                  outline: "none",
                }}
                aria-label="Toggle sidebar"
              >
                <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
                  <rect y="3" width="22" height="3" rx="1.5" fill={iconColor} />
                  <rect y="9" width="22" height="3" rx="1.5" fill={iconColor} />
                  <rect y="15" width="22" height="3" rx="1.5" fill={iconColor} />
                </svg>
              </button>
            )}
            <NavLink
              to="/config"
              className="hidden md:block font-semibold text-sm hover:underline"
              style={{
                color: iconColor,
                textDecoration: "underline",
                marginLeft: 2,
                marginRight: 8,
              }}
            >
              Home
            </NavLink>
            <h2
              className="md:hidden text-lg sm:text-xl font-bold uppercase tracking-wider select-none cursor-pointer"
              style={{
                color: iconColor,
                margin: 0,
                userSelect: "none",
                whiteSpace: "nowrap",
              }}
              onClick={() => navigate("/config")}
            >
              konstruct
            </h2>
          </div>

          {/* Center: Konstruct Logo (Desktop only) */}
          <div className="hidden md:flex shrink-0 justify-center px-2">
            <h2
              className="text-lg sm:text-xl font-bold uppercase tracking-wider select-none cursor-pointer"
              style={{
                color: iconColor,
                margin: 0,
                userSelect: "none",
                whiteSpace: "nowrap",
              }}
              onClick={() => navigate("/config")}
            >
              konstruct
            </h2>
          </div>

          {/* Right Actions */}
          <div className="flex items-center justify-end gap-2 sm:gap-3 flex-1">
            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-3">
              <NavLink
                to="/privacy"
                className="font-medium flex items-center gap-1 text-sm hover:opacity-80"
                style={navLinkStyle}
                title="Privacy Policy"
              >
                🔒
              </NavLink>

              {showProjectHeadDashboard && (
                <button
                  onClick={handleDashboardClick}
                  style={{
                    color: iconColor,
                    background: "transparent",
                    border: "none",
                    fontSize: 20,
                  }}
                  title="Project Head Dashboard"
                  className="hover:opacity-80"
                >
                  <MdOutlineDashboard />
                </button>
              )}

              {allowuser && (
                <button
                  onClick={handleSettingsClick}
                  style={{
                    color: iconColor,
                    background: "transparent",
                    border: "none",
                    fontSize: 20,
                  }}
                  title="Settings"
                  className="hover:opacity-80"
                >
                  <IoSettingsOutline />
                </button>
              )}

              {/* Real-time Notification Bell */}
              <NotificationBell iconColor={iconColor} />

              <button
                onClick={toggleTheme}
                className="p-1.5 transition-colors hover:opacity-80"
                title="Toggle Theme"
              >
                {theme === "dark" ? (
                  <FaSun style={{ color: ORANGE, fontSize: 18 }} />
                ) : (
                  <FaMoon style={{ color: iconColor, fontSize: 18 }} />
                )}
              </button>

              <button
                onClick={() => setIsProfile(true)}
                style={{
                  color: iconColor,
                  background: "transparent",
                  border: "none",
                  fontSize: 20,
                }}
                title="Profile"
                className="hover:opacity-80"
              >
                <FaRegCircleUser />
              </button>
            </div>

            {/* Mobile Actions */}
            <div className="flex items-center gap-2 md:hidden">
              {showProjectHeadDashboard && (
                <button
                  onClick={handleDashboardClick}
                  className="p-2 rounded-lg transition-colors"
                  title="Project Head Dashboard"
                  style={{ color: iconColor }}
                >
                  <MdOutlineDashboard />
                </button>
              )}
              {/* Real-time Notification Bell */}
              <NotificationBell iconColor={iconColor} />
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg transition-colors"
                title="Toggle Theme"
                style={{ color: iconColor }}
              >
                {theme === "dark" ? <FaSun /> : <FaMoon />}
              </button>
              <button
                onClick={() => setIsProfile(true)}
                className="p-2 rounded-lg transition-colors"
                title="Profile"
                style={{ color: iconColor }}
              >
                <FaRegCircleUser />
              </button>
              <button
                onClick={() => setIsMobileMenuOpen((open) => !open)}
                className="rounded-lg flex items-center justify-center"
                style={{
                  background: "transparent",
                  border: `2px solid ${borderColor}`,
                  width: 40,
                  height: 40,
                  color: iconColor,
                }}
                aria-label="Toggle navigation menu"
              >
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <rect y="3" width="22" height="3" rx="1.5" fill={iconColor} />
                  <rect y="9" width="22" height="3" rx="1.5" fill={iconColor} />
                  <rect y="15" width="22" height="3" rx="1.5" fill={iconColor} />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Row: Quality / Safety dropdowns (Desktop only, Centered) */}
        <div
          className="hidden md:flex items-center border-t justify-center w-full"
          style={{
            height: HEADER_NAV_ROW_HEIGHT,
            borderTop: theme === "dark" ? "1px solid #343442" : "1px solid #f2e4d0",
            paddingLeft: sidebarOpen && showHamburger && !isMobile ? SIDEBAR_WIDTH : 0,
            transition: "padding-left 0.35s cubic-bezier(.6,-0.17,.22,1.08)",
          }}
        >
          <div
            className="flex items-center justify-center gap-4 sm:gap-6 px-4 w-full"
          >
            <NavDropdown
              label="Quality"
              items={qualityNavItems}
              textColor={textColor}
              iconColor={iconColor}
              cardColor={cardColor}
              borderColor={borderColor}
              theme={theme}
            />

            {showSafetyForUserOnly && (
              <NavDropdown
                label="Safety"
                items={safetyNavItems}
                textColor={textColor}
                iconColor={iconColor}
                cardColor={cardColor}
                borderColor={borderColor}
                theme={theme}
              />
            )}
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div
          className="fixed left-0 right-0 z-[199] shadow-lg border-b md:hidden"
          style={{
            top: 64,
            background: cardColor,
            borderBottom: `2px solid ${borderColor}`,
          }}
        >
          <div className="flex flex-col px-4 py-3 gap-2 max-h-[70vh] overflow-y-auto">
            <NavLink
              to="/config"
              onClick={() => setIsMobileMenuOpen(false)}
              className="font-medium py-2"
              style={navLinkStyle}
            >
              Home
            </NavLink>

            {showProjectHeadDashboard && (
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleDashboardClick();
                }}
                className="font-medium py-2 text-left"
                style={{ color: iconColor }}
              >
                Project Head Dashboard
              </button>
            )}

            {/* Quality group */}
            <div
              className="text-xs font-bold uppercase tracking-wider pt-2"
              style={{ color: iconColor }}
            >
              Quality
            </div>
            {qualityNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-medium py-2 pl-2"
                style={navLinkStyle}
                title={item.title}
              >
                {item.label}
              </NavLink>
            ))}

            {/* Safety group */}
            {showSafetyForUserOnly && (
              <>
                <div
                  className="text-xs font-bold uppercase tracking-wider pt-2"
                  style={{ color: iconColor }}
                >
                  Safety
                </div>
                {safetyNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="font-medium py-2 pl-2"
                    style={navLinkStyle}
                    title={item.title}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </>
            )}

            <NavLink
              to="/privacy"
              onClick={() => setIsMobileMenuOpen(false)}
              className="font-medium py-2"
              style={navLinkStyle}
            >
              Privacy Policy
            </NavLink>
            {allowuser && (
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleSettingsClick();
                }}
                className="font-medium py-2 text-left"
                style={{ color: iconColor }}
              >
                Settings
              </button>
            )}
          </div>
        </div>
      )}

      {/* Drawers */}
      {isProfile && <Profile onClose={() => setIsProfile(false)} />}
      {isNotification && (
        <Notification onClose={() => setIsNotification(false)} />
      )}
    </>
  );
}

export default Header;